import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, interval, Subject } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { LogType } from '@swagger/humanresources';
import { AsyncTaskEventsService, AsyncTaskLogsPush, HrpSignalRService } from '@core/signalr';
import { StatusSnackbarEvent, StatusSnackbarService } from '@shared/status-snackbar';
import { SnackbarStatusType } from '@shared/ui';
import { PlanningTaskStore } from '@features/planning/data-planning-task';
import {
  failureSummary,
  isRunning,
  PLANNING_TASK_POLL_INTERVAL_MS,
  PlanningTask,
} from '@features/planning/util-planning-task';
import { toPlanLabel } from '../util/plan-name.util';

const VISIBLE: DocumentVisibilityState = 'visible';

/**
 * Follows the planning tasks of this user (#5182): starts finalizing, applies
 * the logs the hub pushes, catches up what a push may have missed (right after
 * the start, after a reconnect, when the tab is visible again, after a reload,
 * and every 30 s while a task runs) and tells the user the outcome.
 * Root-provided like its store: a task outlives the dialog that started it and
 * the planning page; the shell starts it for the planning roles (PR 1785).
 */
@Injectable({ providedIn: 'root' })
export class PlanningTaskFacade {
  readonly #store = inject(PlanningTaskStore);
  readonly #hub = inject(HrpSignalRService);
  readonly #events = inject(AsyncTaskEventsService);
  readonly #snackbar = inject(StatusSnackbarService);
  readonly #transloco = inject(TranslocoService);
  readonly #document = inject(DOCUMENT);
  readonly #finished$ = new Subject<PlanningTask>();
  /** Tasks whose outcome was told; push and catch-up may both deliver the milestone. */
  readonly #told = new Set<string>();

  readonly tasks = this.#store.taskList;
  readonly #runningTasks = this.#store.runningTasks;
  /** Each task once, when it reached its milestone. */
  readonly finished$ = this.#finished$.asObservable();

  constructor() {
    this.#hub.receiveAsyncTaskLogs$
      .pipe(takeUntilDestroyed())
      .subscribe((push) => this.#onPush(push));
    this.#events.resync$.pipe(takeUntilDestroyed()).subscribe(() => void this.#resync());
    interval(PLANNING_TASK_POLL_INTERVAL_MS)
      .pipe(
        filter(() => this.#isPollDue()),
        takeUntilDestroyed(),
      )
      .subscribe(() => void this.#resync());
    void this.#resync();
  }

  /**
   * Whether a running task works on this plan: it references the plan's uId, or
   * it is named after the plan. The task list of hrp-test delivers no references
   * yet, so after a reload the name is the only link (#5182).
   */
  isBeingFinalized(plan: { uId?: string | null; name?: string | null }): boolean {
    return this.#runningTasks().some(
      (task) =>
        (!!plan.uId && task.referenceUIds.includes(plan.uId)) ||
        (!!plan.name && task.planName === plan.name),
    );
  }

  /**
   * Starts finalizing a schedule group; resolves with the tracked task. A refused
   * start throws a PlanningTaskRejectedError with the backend's text.
   */
  async startFinalize(scheduleGroupUId: string, planName: string): Promise<PlanningTask> {
    const task = await this.#store.startFinalize(scheduleGroupUId, planName);
    // A small task may finish before its answer arrives, and its push went unheard: read its
    // logs, but hand the accepted task back at once so the dialog is not locked meanwhile
    // (#5182, PR 1785).
    void this.#catchUp(task.asyncTaskUId);
    return task;
  }

  #onPush(push: AsyncTaskLogsPush): void {
    // Every background task of the user pushes; only tracked planning tasks matter here.
    if (!this.#store.tasks()[push.asyncTaskUId]) return;
    this.#noticeOutcome(this.#store.applyLogs(push.asyncTaskUId, push.logs));
  }

  /** Only while a task runs, and only in a tab the user can see. */
  #isPollDue(): boolean {
    return this.#runningTasks().length > 0 && this.#document.visibilityState === VISIBLE;
  }

  /** The user's running tasks (reload, other tab) and the logs missed meanwhile. */
  async #resync(): Promise<void> {
    try {
      await this.#store.trackRunningTasks();
    } catch (error) {
      console.error('[PlanningTaskFacade] listing the running tasks failed', error);
    }
    await Promise.all(this.#store.runningTasks().map((task) => this.#catchUp(task.asyncTaskUId)));
  }

  async #catchUp(asyncTaskUId: string): Promise<void> {
    try {
      this.#noticeOutcome(await this.#store.catchUpLogs(asyncTaskUId));
    } catch (error) {
      // The next push or resync brings the logs; the task keeps running meanwhile.
      console.error('[PlanningTaskFacade] catching up the logs failed', asyncTaskUId, error);
    }
  }

  #noticeOutcome(task: PlanningTask | null): void {
    if (!task || isRunning(task) || this.#told.has(task.asyncTaskUId)) return;
    this.#told.add(task.asyncTaskUId);
    if (task.outcome?.errorText) {
      // The full text with its stack trace is for the developer, the user gets the summary.
      console.error('[PlanningTaskFacade] task failed', task.asyncTaskUId, task.outcome.errorText);
    }
    this.#snackbar.show(this.#outcomeSnackbar(task));
    this.#finished$.next(task);
  }

  /** Success closes by itself; a failure stays until the user has read it (#5182). */
  #outcomeSnackbar(task: PlanningTask): StatusSnackbarEvent {
    if (task.outcome?.milestone === LogType.Succeeded) {
      return {
        state: SnackbarStatusType.success,
        message: this.#transloco.translate('planingdialog.snVacationFinalize'),
        description: toPlanLabel(task.planName),
      };
    }
    // The dialog may be closed by now, so the reason's first line travels with the snackbar (#5182).
    const parts = [
      toPlanLabel(task.planName),
      this.#transloco.translate('vacation-planning.finalize-failed'),
      failureSummary(task.outcome?.errorText),
    ];
    return {
      state: SnackbarStatusType.error,
      persistent: true,
      message: this.#transloco.translate('planingdialog.snVacationFinalizeError'),
      description: parts.filter((part) => !!part).join(' – '),
    };
  }
}
